// PlacePinTool — click to place a context pin on the canvas
// Reads selected pin type from ui/state, creates pin via zoneState

import type { Tool, ToolContext } from '@/types/editor'
import type { ToolManager } from '../ToolManager'
import { zoneState } from '@/zones/ZoneState'
import { undoStack } from '@/editor/UndoStack'
import { getSelectedPinType, setSelectedPinType, getPlacementMode, getViewer, getToolMode } from '@/ui/state'
import { debug } from '@/utils/debug'

export class PlacePinTool implements Tool {
  readonly name = 'place-pin'
  readonly cursor = 'crosshair'
  readonly hint = 'tool.hint.placePin'

  private toolManager: ToolManager

  constructor(toolManager: ToolManager) {
    this.toolManager = toolManager
  }

  activate(): void {
    // Disable selection/hover during placement
    const selMgr = getViewer()?.getScene()?.getSelectionManager()
    selMgr?.deselect()
    selMgr?.clearHover()
    selMgr?.setEnabled(false)
    debug.log('PlacePinTool', `Activated (mode: ${getPlacementMode()})`)
  }

  deactivate(): void {
    if (getToolMode() === 'pointer') {
      getViewer()?.getScene()?.getSelectionManager()?.setEnabled(true)
    }
    debug.log('PlacePinTool', 'Deactivated')
  }

  onMouseDown(ctx: ToolContext): void {
    if (ctx.event.button !== 0) return
    if (!ctx.editorPoint) return

    const pinType = getSelectedPinType()
    if (!pinType) {
      debug.warn('PlacePinTool', 'No pin type selected')
      return
    }

    const { x, y } = ctx.editorPoint
    const id = zoneState.addPin(pinType, x, y)
    const snapshot = { ...zoneState.getPinById(id)! }

    undoStack.push({
      description: `Create pin ${id}`,
      undo: () => zoneState.deletePin(id),
      redo: () => zoneState.restorePin(snapshot),
    })

    debug.log('PlacePinTool', `Placed ${pinType} pin: ${id} at (${x}, ${y})`)

    // In single mode, deselect and return to select tool
    if (getPlacementMode() === 'single') {
      setSelectedPinType(null)
      this.toolManager.setActiveTool('select')
    }

    ctx.event.preventDefault()
    ctx.event.stopPropagation()
  }

  onMouseMove(_ctx: ToolContext): void {
    // No preview needed for pin placement
  }

  onMouseUp(_ctx: ToolContext): void {
    // Creation happens on mouseDown
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      setSelectedPinType(null)
      this.toolManager.setActiveTool('select')
    }
  }

  dispose(): void {
    // Nothing to clean up
  }
}
